// Libraries
import 'core-js/stable';
import 'regenerator-runtime/runtime';
import 'url-polyfill';
import 'raf/polyfill';
import 'proxy-polyfill/proxy.min';
import amplitude from 'amplitude-js';
import React, { Suspense } from 'react';
import ReactDOM from 'react-dom';
import { BrowserRouter, Route, Switch } from 'react-router-dom';
import * as Sentry from '@sentry/browser';
import { ExtraErrorData, HttpClient } from '@sentry/integrations';

// i18n
import i18n from 'i18n';

// Utilities
import { rtlLanguage } from 'common/utils/helpers';
import { logAmplitudeEvent } from 'common/utils/analytics';
import { useGoogleTracking } from 'common/hooks/useGoogleTracking';

// Routes
import Routes from 'routes';

// Components
import ScrollToTop from 'common/components/ScrollToTop';
import NotFoundPageContainer from 'common/components/NotFoundPageContainer';
import EngagementReportContainer from 'engagementReport/components/base/EngagementReportContainer';
import { I18nextProvider } from 'react-i18next';

const envTest = /^v[0-9]{1,3}.[0-9]{1,3}.[0-9]{1,3}/;
let viteEnv = 'production';

if (!envTest.test(import.meta.env.VITE_CIVIL_SPACE_VERSION)) {
  viteEnv = 'staging-uat';
}

if (import.meta.env.DEV) {
  viteEnv = 'development';
}

Sentry.init({
  dsn: import.meta.env.VITE_SENTRY_DSN,
  release: `rails-${import.meta.env.VITE_CIVIL_SPACE_VERSION}`,
  environment: viteEnv,
  integrations: [new ExtraErrorData(), new HttpClient(), new Sentry.BrowserTracing()],
  sendDefaultPii: true,
  tracesSampleRate: 0.05,
});

amplitude.getInstance().init(window.gon?.amplitudeApiKey, undefined, {
  batchEvents: true,
  eventUploadPeriodMillis: 100,
});
window.amplitudeInstance = amplitude.getInstance();

// Log events triggered from rails
if (window.gon?.analyticsEvents) window.gon.analyticsEvents.forEach(logAmplitudeEvent);

const el = document.querySelector('#react-app');

if (gon.theme) {
  document.documentElement.classList.add(`theme-${gon.theme}`);
}

if (rtlLanguage()) {
  document.documentElement.classList.add('rtl');
}

const App = () => {
  useGoogleTracking(window.googleTrackingIds);

  return (
    <div className="app">
      <Switch>
        <Route exact strict path={Routes.ENGAGEMENT_REPORT} component={EngagementReportContainer} />
        <Route component={NotFoundPageContainer} />
      </Switch>
    </div>
  );
};

ReactDOM.render(
  <I18nextProvider i18n={i18n}>
    <BrowserRouter>
      <ScrollToTop>
        <Suspense fallback={null}>
          <App />
        </Suspense>
      </ScrollToTop>
    </BrowserRouter>
  </I18nextProvider>,
  el
);
